// src/pages/FollowingLeaderboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../components/AuthGate";
import { fetchFollowingUsers } from "../api";

const fmt = (n, d = 2) => (n == null || Number.isNaN(+n) ? "—" : (+n).toFixed(d));

export default function FollowingLeaderboard() {
  const { user, initializing } = useAuth();
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("roi"); // "roi" | "winrate" | "profit" | "picks"

  useEffect(() => {
    if (!user) {
      setFollowing([]);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const users = await fetchFollowingUsers();
        if (!cancelled) {
          setFollowing(Array.isArray(users) ? users : []);
        }
      } catch (err) {
        console.error("Failed to load following users:", err);
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load following list");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const sorted = useMemo(() => {
    const users = [...following];

    users.sort((a, b) => {
      const aStats = a.bet_stats || {};
      const bStats = b.bet_stats || {};

      switch (sortBy) {
        case "roi":
          return (bStats.roi || 0) - (aStats.roi || 0);
        case "winrate":
          return (bStats.win_rate || 0) - (aStats.win_rate || 0);
        case "profit":
          return (bStats.profit || 0) - (aStats.profit || 0);
        case "picks":
          return (bStats.total_bets || 0) - (aStats.total_bets || 0);
        default:
          return 0;
      }
    });

    return users;
  }, [following, sortBy]);

  if (initializing || loading) {
    return (
      <div className="page" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <p>Loading leaderboard...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{
          background: "#0a0f0c",
          padding: 40,
          borderRadius: 16,
          textAlign: "center",
          border: "1px solid rgba(255,255,255,0.08)"
        }}>
          <h2>Following Leaderboard</h2>
          <p style={{ marginBottom: 20, opacity: 0.7 }}>
            See how the people you follow are performing
          </p>
          <Link
            to="/login"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              borderRadius: 999,
              background: "#34d399",
              color: "#000",
              fontWeight: 700,
              textDecoration: "none"
            }}
          >
            Log in to view
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Following Leaderboard</h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, opacity: 0.7 }}>
          See how the people you follow are performing with their picks
        </p>
      </div>

      {error && (
        <div style={{
          background: "rgba(210,59,59,0.1)",
          border: "1px solid rgba(210,59,59,0.3)",
          borderRadius: 12,
          padding: 20,
          color: "#d23b3b",
          marginBottom: 20
        }}>
          {error}
        </div>
      )}

      {following.length === 0 && !error && (
        <div style={{
          background: "#0a0f0c",
          padding: 40,
          borderRadius: 16,
          textAlign: "center",
          border: "1px solid rgba(255,255,255,0.08)"
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
          <h3 style={{ margin: "0 0 12px", fontSize: 20 }}>Not following anyone yet</h3>
          <p style={{ margin: "0 0 24px", opacity: 0.7, fontSize: 14 }}>
            Follow users to see them on the leaderboard
          </p>
          <Link
            to="/tipsters"
            style={{
              display: "inline-block",
              padding: "12px 24px",
              borderRadius: 999,
              background: "#34d399",
              color: "#000",
              fontWeight: 700,
              textDecoration: "none"
            }}
          >
            Discover Users
          </Link>
        </div>
      )}

      {following.length > 0 && (
        <>
          {/* Sort Controls */}
          <div style={{
            display: "flex",
            gap: 12,
            marginBottom: 20,
            overflowX: "auto",
            paddingBottom: 8
          }}>
            {[
              { key: "roi", label: "ROI" },
              { key: "winrate", label: "Win Rate" },
              { key: "profit", label: "Profit" },
              { key: "picks", label: "Picks" }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 999,
                  border: sortBy === key ? "1px solid #34d399" : "1px solid rgba(255,255,255,0.2)",
                  background: sortBy === key ? "rgba(52,211,153,0.15)" : "transparent",
                  color: sortBy === key ? "#34d399" : "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s"
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Leaderboard Table */}
          <div style={{
            background: "#0a0f0c",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden"
          }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>Rank</th>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>User</th>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>Picks</th>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>Win Rate</th>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>ROI</th>
                    <th style={{
                      padding: "16px 20px",
                      textAlign: "right",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      borderBottom: "1px solid rgba(255,255,255,0.08)"
                    }}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((u, idx) => {
                    const stats = u.bet_stats || {};
                    const rank = idx + 1;
                    const isTop3 = rank <= 3;

                    return (
                      <tr
                        key={u.firebase_uid || u.username || idx}
                        style={{
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          transition: "background 0.2s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "16px 20px" }}>
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: isTop3 ? "rgba(52,211,153,0.2)" : "rgba(255,255,255,0.05)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 14,
                            fontWeight: 700,
                            color: isTop3 ? "#34d399" : "#fff"
                          }}>
                            {rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : rank}
                          </div>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <Link
                            to={`/users/${u.username || u.firebase_uid}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              textDecoration: "none",
                              color: "inherit"
                            }}
                          >
                            {u.avatar_url ? (
                              <img
                                src={u.avatar_url}
                                alt={u.display_name || u.username}
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  border: "2px solid rgba(52,211,153,0.2)"
                                }}
                              />
                            ) : (
                              <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                background: "rgba(52,211,153,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#34d399"
                              }}>
                                {(u.display_name || u.username || "U")[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 15 }}>
                                {u.display_name || u.username || "User"}
                              </div>
                              {u.username && (
                                <div style={{ fontSize: 13, opacity: 0.6 }}>
                                  @{u.username}
                                </div>
                              )}
                            </div>
                          </Link>
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "right", fontWeight: 600 }}>
                          {stats.total_bets || 0}
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "right", fontWeight: 600 }}>
                          {fmt((stats.win_rate || 0) * 100, 1)}%
                        </td>
                        <td style={{
                          padding: "16px 20px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: (stats.roi || 0) >= 0 ? "#1db954" : "#d23b3b"
                        }}>
                          {(stats.roi || 0) >= 0 ? "+" : ""}{fmt((stats.roi || 0) * 100, 1)}%
                        </td>
                        <td style={{
                          padding: "16px 20px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: (stats.profit || 0) >= 0 ? "#1db954" : "#d23b3b"
                        }}>
                          {(stats.profit || 0) >= 0 ? "+" : ""}{fmt(stats.profit || 0, 2)} units
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
